import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import * as AuthSelectors from '../../auth/store/auth.selectors';
import { RoleNames } from '../api/types/enums';
import { PermissionService } from '../services/permission.service';

/**
 * Structural directive that conditionally includes a template based on user access levels.
 * This directive checks if the user has Owner or Editor access for the specified roles.
 *
 * Usage:
 * <button *appHasAccess="RoleNames.products">Edit Product</button>
 * <div *appHasAccess="[RoleNames.products, RoleNames.pricing]">Edit products OR pricing</div>
 * <div *appHasAccess="[RoleNames.products, RoleNames.pricing]; requireAll: true">Edit products AND pricing</div>
 */
@Directive({
    selector: '[appHasAccess]',
    standalone: true
})
export class HasAccessDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private hasView = false;
  private currentRoles: RoleNames | RoleNames[] = [];
  private requireAll = false;

  @Input() set appHasAccess(roles: RoleNames | RoleNames[]) {
    this.currentRoles = roles;
    this.updateView();
  }

  @Input() set appHasAccessRequireAll(require: boolean) {
    this.requireAll = require;
    this.updateView();
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private store: Store,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    // Subscribe to role changes and update view accordingly
    this.store
      .select(AuthSelectors.selectAuthRoles)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(): void {
    if (!this.currentRoles) {
      this.showTemplate();
      return;
    }

    // For access, we always use methods that check for Owner/Editor access
    const hasAccess = this.requireAll
      ? this.permissionService.hasAllEditAccess(this.currentRoles)
      : this.permissionService.hasAnyEditAccess(this.currentRoles);

    if (hasAccess && !this.hasView) {
      this.showTemplate();
    } else if (!hasAccess && this.hasView) {
      this.hideTemplate();
    }
  }

  private showTemplate(): void {
    if (!this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    }
  }

  private hideTemplate(): void {
    if (this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
